import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n/index.ts';
import { Icon } from './Icon.tsx';
import { Button } from './ui.tsx';

interface DetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
export const hasDetector = () => typeof window !== 'undefined' && 'BarcodeDetector' in window;

/** The phone's camera, reading codes where the browser can. Each code is reported once until another is seen. */
export function CameraScan({ onCode, onClose }: { onCode: (code: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer = 0;
    let last = '';
    let lastAt = 0;
    const Ctor = (window as unknown as { BarcodeDetector: new (o?: { formats?: string[] }) => DetectorLike }).BarcodeDetector;
    const detector = new Ctor();
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        if (!video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        const tick = async () => {
          if (video.current && video.current.readyState >= 2) {
            try {
              const found = await detector.detect(video.current);
              const code = found[0]?.rawValue;
              const now = Date.now();
              if (code && (code !== last || now - lastAt > 2000)) {
                last = code;
                lastAt = now;
                onCode(code);
              }
            } catch {
              // A frame the detector could not read: try the next one.
            }
          }
          timer = window.setTimeout(tick, 250);
        };
        void tick();
      } catch {
        setError(t('bc.camera_denied'));
      }
    })();
    return () => {
      window.clearTimeout(timer);
      stream?.getTracks().forEach((tr) => tr.stop());
    };
  }, []);
  return (
    <div className="bc-camera">
      {error ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{error}</span>
        </p>
      ) : (
        <div className="bc-video">
          <video ref={video} muted playsInline aria-label={t('bc.camera')} />
          <span className="bc-aim" aria-hidden="true" />
        </div>
      )}
      <Button size="sm" variant="ghost" icon="x" onClick={onClose}>
        {t('bc.camera_off')}
      </Button>
    </div>
  );
}

